import SecondaryButton from 'core-system/Button/SecondaryButton'
import Dropdown from 'core-system/Dropdown'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import TextField from 'core-system/TextField'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useEffect, useMemo, useState } from 'react'
import {
  FleetCardSummary,
  FleetCardType,
} from 'redux/employee/employeeTypes'
import styled from 'styled-components'
import { getAvailableFleetCardTypeItems } from '../utils'

const Subtitle = styled(Text)`
  margin-top: -0.5rem;
  margin-bottom: 1.25rem;
`

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
`

interface AddFleetCardModalProps {
  open: boolean
  existingCards: FleetCardSummary[]
  shippingAddress?: string | null
  isSubmitting?: boolean
  onClose: () => void
  onAdd?: (cardType: FleetCardType, shippingAddress?: string) => void
}

const AddFleetCardModal = React.memo((props: AddFleetCardModalProps) => {
  const {
    open,
    existingCards,
    shippingAddress = null,
    isSubmitting = false,
    onClose,
    onAdd,
  } = props
  const [cardType, setCardType] = useState<FleetCardType | null>(null)
  const [shipTo, setShipTo] = useState(shippingAddress || '')

  const typeItems = useMemo(
    () => getAvailableFleetCardTypeItems(existingCards),
    [existingCards]
  )

  useEffect(() => {
    setCardType(null)
    setShipTo(shippingAddress || '')
  }, [open, shippingAddress])

  const handleAdd = () => {
    if (isSubmitting || !cardType) {
      return
    }
    if (cardType === 'physical' && !shipTo.trim()) {
      return
    }
    onAdd?.(
      cardType,
      cardType === 'physical' ? shipTo.trim() : undefined
    )
  }

  return (
    <Modal open={open} onClose={onClose} width={pxToRem(420)}>
      <ModalHeader title='Add Fleet Card' onClose={onClose} />
      <ModalBody>
        <Subtitle variant='body1' textColor={palette.text.secondary}>
          Choose a card type to issue for this commuter
        </Subtitle>
        {typeItems.length === 0 ? (
          <Text variant='body1' textColor={palette.text.secondary}>
            This commuter already has a physical and virtual card.
          </Text>
        ) : (
          <FieldStack>
            <Dropdown
              label='Card type'
              placeholder='Select a type...'
              active={cardType}
              items={typeItems}
              itemCallback={(item) => setCardType(item.id as FleetCardType)}
              disabled={isSubmitting}
              width='100%'
            />
            {cardType === 'physical' ? (
              <TextField
                label='Ship to'
                value={shipTo}
                onChange={(e) => setShipTo(e.currentTarget.value)}
                disabled={isSubmitting}
                width='100%'
              />
            ) : null}
          </FieldStack>
        )}
      </ModalBody>
      <ModalFooter>
        <FooterActions>
          <SecondaryButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </SecondaryButton>
          <SecondaryButton
            onClick={handleAdd}
            disabled={
              isSubmitting ||
              !cardType ||
              typeItems.length === 0 ||
              (cardType === 'physical' && !shipTo.trim())
            }
          >
            Add Card
          </SecondaryButton>
        </FooterActions>
      </ModalFooter>
    </Modal>
  )
})

if (process.env.NODE_ENV !== 'production') {
  AddFleetCardModal.displayName = 'AddFleetCardModal'
}

export default AddFleetCardModal
